import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useResetPasswordMutation } from "@/features/organizers/api";
import { ApiError } from "@/lib/api-client";
import { useT } from "@/lib/i18n";

const schema = z
  .object({
    password: z.string().min(8, "validation.passwordMin"),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ["confirm"],
    message: "validation.passwordMismatch",
  });

type FormValues = z.infer<typeof schema>;

const LINK_ERROR_CODES = new Set(["invalid_reset_token", "reset_token_expired"]);

/**
 * Sıfırlama linki ekranı. URL'de `?token=...` bekler. Başarılıysa backend tüm
 * session'ları kapatır; kullanıcı yeni şifresiyle giriş yapsın diye /login'e döner.
 */
export function ResetPasswordPage() {
  const t = useT("auth");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const reset = useResetPasswordMutation();
  const token = searchParams.get("token") ?? "";

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });

  async function onSubmit(values: FormValues) {
    try {
      await reset.mutateAsync({ token, password: values.password });
      qc.clear();
      toast.success(t("resetPage.success"));
      navigate("/login", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && LINK_ERROR_CODES.has(err.code)) {
        toast.error(err.detail, {
          duration: 10000,
          action: {
            label: t("resetPage.requestNew"),
            onClick: () => navigate("/forgot-password"),
          },
        });
        return;
      }
      if (err instanceof ApiError && err.status === 429) {
        toast.error(t("tooManyAttempts"));
        return;
      }
      toast.error(err instanceof ApiError ? err.detail : t("resetPage.failed"));
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <CardTitle>{t("resetPage.invalidTitle")}</CardTitle>
            <CardDescription>{t("resetPage.invalidDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" className="w-full">
              <Link to="/forgot-password">{t("resetPage.requestNew")}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl">{t("resetPage.title")}</CardTitle>
          <CardDescription>{t("resetPage.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("resetPage.newPassword")}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("resetPage.confirm")}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={reset.isPending}>
                {reset.isPending ? t("resetPage.submitting") : t("resetPage.submit")}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
